import {
  getUiDatabase,
  requestToPromise,
  transactionDone,
} from "@/storage/uiDatabase";

export interface ScopedStoredRecord {
  uuid: string;
  storageKey: string;
  scopeKey: string;
  syncedAt: string;
}

interface ScopedRepositoryOptions<TEntity, TStored extends TEntity> {
  emptySnapshot: readonly TStored[];
  sortRecords: (records: readonly TStored[]) => TStored[];
  storeName: string;
}

interface ScopedRepositoryChange {
  scopeKey: string;
  table: string;
}

type ScopedRepositoryListener = (change: ScopedRepositoryChange) => void;

export function createScopedUiRepository<
  TEntity extends { uuid: string },
  TStored extends TEntity & ScopedStoredRecord,
>({
  emptySnapshot,
  sortRecords,
  storeName,
}: ScopedRepositoryOptions<TEntity, TStored>) {
  const snapshots = new Map<string, readonly TStored[]>();
  const listeners = new Set<ScopedRepositoryListener>();

  function getSnapshot(scopeKey: string | null): readonly TStored[] {
    if (!scopeKey) return emptySnapshot;

    return snapshots.get(scopeKey) ?? emptySnapshot;
  }

  function subscribe(scopeKey: string, onStoreChange: () => void): () => void {
    const listener: ScopedRepositoryListener = (change) => {
      if (change.table === storeName && change.scopeKey === scopeKey) {
        onStoreChange();
      }
    };

    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }

  async function hydrate(scopeKey: string): Promise<void> {
    setSnapshot(scopeKey, await readRecords(scopeKey));
  }

  async function replace(
    scopeKey: string,
    entities: readonly TEntity[],
  ): Promise<void> {
    const syncedAt = new Date().toISOString();
    const records = sortRecords(entities.map((entity) => (
      toStoredRecord(scopeKey, entity, syncedAt)
    )));

    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    const index = store.index("by_scope");
    const existingRecords = await requestToPromise<TStored[]>(index.getAll(scopeKey));
    const incomingByUuid = new Map(records.map((record) => [record.uuid, record]));

    existingRecords.forEach((record) => {
      const incoming = incomingByUuid.get(record.uuid);
      if (!incoming) {
        store.delete(record.storageKey);
        return;
      }

      if (!areStoredRecordsEqual(record, incoming)) {
        store.put(incoming);
      }

      incomingByUuid.delete(record.uuid);
    });

    incomingByUuid.forEach((record) => {
      store.put(record);
    });

    await transactionDone(transaction);

    if (!areStoredRecordListsEqual(getSnapshot(scopeKey), records)) {
      setSnapshot(scopeKey, records);
    }
  }

  async function upsert(scopeKey: string, entity: TEntity): Promise<void> {
    const record = toStoredRecord(scopeKey, entity, new Date().toISOString());
    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).put(record);

    await transactionDone(transaction);
    setSnapshot(
      scopeKey,
      sortRecords([
        ...getSnapshot(scopeKey).filter((current) => current.uuid !== entity.uuid),
        record,
      ]),
    );
  }

  /**
   * Writes a whole set of records at once.
   *
   * A subtree is stored in one transaction and announced once, rather than a
   * transaction and a re-render per component: building a page out of a clone
   * or a manifest costs a single update however many components it holds.
   */
  async function upsertMany(
    scopeKey: string,
    entities: readonly TEntity[],
  ): Promise<void> {
    if (!entities.length) return;

    const syncedAt = new Date().toISOString();
    const records = entities.map((entity) => (
      toStoredRecord(scopeKey, entity, syncedAt)
    ));
    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    records.forEach((record) => store.put(record));

    await transactionDone(transaction);

    const writtenUuids = new Set(records.map((record) => record.uuid));
    setSnapshot(
      scopeKey,
      sortRecords([
        ...getSnapshot(scopeKey).filter(
          (current) => !writtenUuids.has(current.uuid),
        ),
        ...records,
      ]),
    );
  }

  async function remove(scopeKey: string, uuid: string): Promise<void> {
    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).delete(`${scopeKey}:${uuid}`);

    await transactionDone(transaction);
    setSnapshot(
      scopeKey,
      getSnapshot(scopeKey).filter((record) => record.uuid !== uuid),
    );
  }

  /**
   * Removes a whole set of records at once.
   *
   * Deleting a subtree is one transaction and one announcement, for the same
   * reason writing one is: the tree and the canvas are drawn again on every
   * announcement.
   */
  async function removeMany(
    scopeKey: string,
    uuids: readonly string[],
  ): Promise<void> {
    if (!uuids.length) return;

    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    const store = transaction.objectStore(storeName);
    uuids.forEach((uuid) => store.delete(`${scopeKey}:${uuid}`));

    await transactionDone(transaction);

    const removedUuids = new Set(uuids);
    setSnapshot(
      scopeKey,
      getSnapshot(scopeKey).filter((record) => !removedUuids.has(record.uuid)),
    );
  }

  async function clear(): Promise<void> {
    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).clear();

    await transactionDone(transaction);

    const affectedScopeKeys = [...snapshots.keys()];
    snapshots.clear();
    affectedScopeKeys.forEach(emitChange);
  }

  async function readRecords(scopeKey: string): Promise<TStored[]> {
    const database = await getUiDatabase();
    const transaction = database.transaction(storeName, "readonly");
    const store = transaction.objectStore(storeName);
    const index = store.index("by_scope");
    const records = await requestToPromise<TStored[]>(index.getAll(scopeKey));

    await transactionDone(transaction);
    return sortRecords(records);
  }

  function setSnapshot(scopeKey: string, records: readonly TStored[]): void {
    snapshots.set(scopeKey, records);
    emitChange(scopeKey);
  }

  function emitChange(scopeKey: string): void {
    const change: ScopedRepositoryChange = {
      scopeKey,
      table: storeName,
    };

    listeners.forEach((listener) => listener(change));
  }

  function toStoredRecord(
    scopeKey: string,
    entity: TEntity,
    syncedAt: string,
  ): TStored {
    return {
      ...entity,
      storageKey: `${scopeKey}:${entity.uuid}`,
      scopeKey,
      syncedAt,
    } as TStored;
  }

  return {
    getSnapshot,
    hydrate,
    clear,
    remove,
    removeMany,
    replace,
    subscribe,
    upsert,
    upsertMany,
  };
}

function areStoredRecordListsEqual<TRecord extends ScopedStoredRecord>(
  left: readonly TRecord[],
  right: readonly TRecord[],
): boolean {
  if (left.length !== right.length) return false;

  return left.every((leftRecord, index) => {
    const rightRecord = right[index];
    if (!rightRecord) return false;

    return areStoredRecordsEqual(leftRecord, rightRecord);
  });
}

function areStoredRecordsEqual<TRecord extends ScopedStoredRecord>(
  left: TRecord,
  right: TRecord,
): boolean {
  return (
    JSON.stringify(dropSyncMetadata(left))
    === JSON.stringify(dropSyncMetadata(right))
  );
}

function dropSyncMetadata<TRecord extends ScopedStoredRecord>(
  record: TRecord,
): Omit<TRecord, "scopeKey" | "storageKey" | "syncedAt"> {
  const {
    scopeKey,
    storageKey,
    syncedAt,
    ...data
  } = record;

  return data;
}
